// /buildingdirectories — the printed QR signage each faculty places in its own
// buildings, ported from UMapsTools' Campus Editor QR tool.
//
// A student walks in an entrance, scans the sign, and the pathfinder opens with
// FROM already pinned to that nav node; all they supply is where they are going
// and their accessibility / tunnel preferences.
//
// Password-gated per faculty (BUILDING_EDITORS + BUILDING_SCOPES, via
// /api/auth with scope:"buildings"). A faculty may place signs only in the
// buildings its account is scoped to — enforced on the server, since a check
// that only exists in the browser is not a check.
//
// THE MAP IS GOOGLE MAPS, over the live pathfinder snapshot. The internal tool
// draws a bare canvas of the `.umapsraw` because the person using it is placing
// dozens of signs from a desk; here the map is satellite imagery, which is what
// makes a building recognisable to somebody who administers it rather than drew
// it. The PRINTED guide still uses a straightened floor plan (see planFrame.js)
// — that is a different job, done on paper, by somebody walking a corridor.
//
// Overlays are built imperatively (`new google.maps.Marker/Polygon`), never as
// declarative @react-google-maps children: those are broken in this codebase and
// would also stutter restyling several hundred markers as signs are placed.
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import AccountMaker, { ACCOUNT_MAKER_CSS } from './components/AccountMaker.jsx';
import UMapsWordmark from './components/UMapsWordmark.jsx';
import { initialToken, clearToken, writeAdminToken } from './utils/adminSession.js';
import { planFloor, planSvg } from './utils/signPlanSvg.js';
import { buildSignSheet, signQrSvg, LOGO_URL } from './utils/signSheet.js';
import { MARK_CSS_WIDTH, loadBadgeDataUrl } from './utils/qrBadge.js';
import {
  signUrl, suggestCode, makeSign, nodeName, floorTag, prettifySlug, DEFAULT_BASE,
} from './utils/signs.js';

const MAPS_KEY = process.env.REACT_APP_MAPS_KEY;
// In dev the API runs on the Express server (port 3001); in prod it's same-origin.
const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
// The snapshot folder name has a space ("Map Info") and it MUST stay
// percent-encoded: Safari/WebKit's fetch() rejects a raw space with "The string
// did not match the expected pattern", while Chrome silently encodes it — so a
// raw space here works on the machine it was written on and loads nothing on
// iOS. Same note guards the pathfinder's own base URL in PathfinderPage.js.
const DATA = '/Map%20Info/pathfinder';
const TOKEN_KEY = 'umaps-bd-token';

const CENTER = { lat: 49.80939016935401, lng: -97.1343494222327 };

// The pseudo-buildings carry nav graphs but no floor plans, and nobody posts a
// sign "in outdoors".
//
// ⚠️ `tunnels` is NOT in here any more, and it is the one that behaves
// differently: its 25 waypoints are real places somebody stands, under campus,
// and they are exactly where a QR sign belongs — a tunnel junction is the point
// in the whole system where a student is most lost and least able to see a
// landmark. It is kept out of the ordinary building LIST (there is no faculty
// that administers the tunnels, and a scoped account would never be given them)
// and reached through the campus view below instead, which is admin-only.
const PSEUDO = new Set(['outdoors', 'building_connections', 'parking']);

// The tunnel network, which is a building to the data and a place to a person.
const TUNNELS = 'tunnels';

// ── The campus view ─────────────────────────────────────────────────────────
//
// ⚠️ 40 buildings is 7.5 MB of nav JSON and 3,698 nodes on floor 1 alone, and
// neither number is one you can put on a Google Map and hope. Both are measured
// rather than guessed, and both shape what follows:
//
//  · Nav files load PROGRESSIVELY, into the same `geoRef` cache the
//    single-building view fills, so the two warm each other and a second visit
//    costs nothing.
//  · Markers are culled to the VIEWPORT and capped. Drawing 3,698 markers is
//    not slow, it is unusable — the existing code already notes that restyling
//    several hundred stutters.
//
// Tunnel nodes are exempt from both: there are 25 of them, they are the subject
// of this view, and they must never vanish because the map is pulled back.
const CAMPUS_FLOOR = 1;
const CAMPUS_MARKER_CAP = 1200;
// Below this the building nodes are noise at campus scale — you cannot tell
// which of four dots is the one you mean — so only the tunnels are drawn and
// the map reads as the network it is.
const CAMPUS_NODE_ZOOM = 18;

// One colour for a node and one for a sign — the per-type palette that used to
// be here was answering a question this tool does not ask, and it has not been
// read since the markers went to a single design.
//
// The node keeps the toolchain's blue, so a node looks the same wherever you
// meet it. A placed sign is the darker UM blue and twice the size, and the two
// never have to be told apart at a glance: a sign is a fact about the map, a
// node is only a place one could go.
const NODE_COLOR = '#1a73e8';
// The tunnels, which are not a building and should not read as one. Amber
// against the blue nodes and the white footprints: the tunnel network is the
// subject of the campus view, and it runs UNDER everything else on screen, so
// it has to be told apart from the floor above it at a glance.
const TUNNEL_COLOR = '#f29900';
// Structure polygons — the shared map scheme (UMAPS_BRANDING_GUIDE.md §5).
const POLY_OPTS = {
  outline:  { fill: '#ffffff', op: 1,    stroke: '#aaaaaa', w: 2 },
  room:     { fill: '#FCF8EF', op: 0.8,  stroke: '#dbdbdb', w: 1.5 },
  stair:    { fill: '#c4956a', op: 0.85, stroke: '#a0724a', w: 1.5 },
  elevator: { fill: '#b0bec5', op: 0.85, stroke: '#78909c', w: 1.5 },
  blocked:  { fill: '#000000', op: 0.85, stroke: '#000000', w: 1.5 },
  tunnel:   { fill: '#efebe9', op: 0.9,  stroke: '#6d4c41', w: 2 },
};
// UM Blue, from UMapsTools' UMAPS_BRANDING_GUIDE.md §2. The map is satellite
// imagery in every theme, so this one is fixed rather than themed — the legend
// describes the map, not the page around it.
const SIGN_COLOR = '#0060a8';
// UM Gold (branding guide §2) — the one warm mark on a blue map, so what you
// last clicked is findable without hunting.
const SEL_COLOR = '#fcb40c';

const THEME_KEY = 'umaps-bd-theme';

/**
 * Fetch one JSON file out of the snapshot.
 *
 * An SPA host answers a MISSING file with index.html and a 200 rather than a
 * 404, so `res.json()` blows up on markup and the real fault ("that file isn't
 * deployed") arrives disguised as a parse error. Checking the content type turns
 * it back into a plain "not there". Same trap is handled in the pathfinder's own
 * dataSource.js.
 */
async function snapshotJson(path) {
  const res = await fetch(`${DATA}/${path}`);
  if (!res.ok) return null;
  const ctype = (res.headers.get('content-type') || '').toLowerCase();
  if (ctype.includes('text/html')) return null;
  return res.json();
}

/**
 * What the floor stack says: F100, F200 — the way rooms on that floor are
 * numbered, and the way somebody who works in the building refers to it.
 *
 * Display only. `floorTag` still supplies the F1/F2 inside a sign's identifier,
 * because those are printed on walls: renaming a floor here must never change
 * what E1-F1A means.
 */
function floorLabel(f) {
  return f >= 1 ? `F${f}00` : floorTag(f);
}

/**
 * Is a node inside a polygon's ring? Plain ray casting on lat/lng.
 *
 * Written out rather than reaching for `geometry.poly.containsLocation`, which
 * would mean loading Maps' geometry library — a second script on a page that
 * needs this for one thing, on rings small enough that the flat-earth error is
 * far below the width of a doorway.
 */
function pointInRing(pt, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const yi = ring[i].latitude, xi = ring[i].longitude;
    const yj = ring[j].latitude, xj = ring[j].longitude;
    if ((yi > pt.lat) !== (yj > pt.lat)
        && pt.lng < ((xj - xi) * (pt.lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * Light or dark, top right. A switch rather than a pair of buttons: there are
 * exactly two states and it is always in one of them, so a track that slides is
 * the whole control — nothing to read, and the knob says which side you are on.
 */
function ThemeSwitch({ theme, onChange }) {
  const light = theme === 'light';
  return (
    <button type="button" className={`bd-theme ${light ? 'on' : ''}`}
            role="switch" aria-checked={light} aria-label="Light mode"
            title={light ? 'Switch to dark' : 'Switch to light'}
            onClick={() => onChange(light ? 'dark' : 'light')}>
      <svg className="bd-theme-ico sun" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="4.4" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map(d => (
          <line key={d} x1="12" y1="2.6" x2="12" y2="5.2" transform={`rotate(${d} 12 12)`} />
        ))}
      </svg>
      <svg className="bd-theme-ico moon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20 14.2A8.2 8.2 0 0 1 9.8 4 8.4 8.4 0 1 0 20 14.2Z" />
      </svg>
      <span className="bd-theme-knob" />
    </button>
  );
}

export default function BuildingDirectoriesPage() {
  // ── Session ────────────────────────────────────────────────────────────────
  // Own account first, the operator's session second — a faculty signing in on a
  // machine where somebody had been the admin must get their own scope.
  const [token, setToken] = useState(() => initialToken(TOKEN_KEY));
  const [user, setUser] = useState('');
  const [scope, setScope] = useState([]);          // '*' or [slug]
  const [isAdmin, setIsAdmin] = useState(false);   // the central operator account
  const [page, setPage] = useState('signs');       // 'signs' | 'accounts'
  const [username, setUsername] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const saveToken = useCallback((t) => {
    setToken(t);
    if (!t) { clearToken(TOKEN_KEY); return; }
    try { sessionStorage.setItem(TOKEN_KEY, t); } catch { /* private mode */ }
  }, []);

  const login = async (e) => {
    e?.preventDefault();
    setErr(''); setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/auth`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password: pw, scope: 'buildings' }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.token) { setErr(j.error || 'Login failed.'); setBusy(false); return; }
      saveToken(j.token); setUser(j.user || ''); setScope(j.buildings ?? '*');
      setIsAdmin(!!j.admin); setPw('');
      // Signing in as the operator here should carry on to the next tool as
      // readily as arriving from /admin carries in.
      if (j.admin) writeAdminToken(j.token);
    } catch { setErr('Network error.'); }
    setBusy(false);
  };

  const api = useCallback(async (qs = '', body = null) => {
    const r = await fetch(`${API_BASE}/api/building-signs${qs}`, {
      method: body ? 'POST' : 'GET',
      headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (r.status === 401) { saveToken(''); throw new Error('Session expired — log in again.'); }
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `Request failed (${r.status})`);
    return j;
  }, [token, saveToken]);

  // ── Data ───────────────────────────────────────────────────────────────────
  const [manifest, setManifest] = useState([]);
  const [signs, setSigns] = useState([]);          // every sign on campus
  const [status, setStatus] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [view, setView] = useState('place');       // 'place' | 'directory' | 'print'
  // The admin-only campus view: the tunnel network plus every building's first
  // floor, on one map. Not a `view` value — it replaces which BUILDING is being
  // looked at, not which pane, and Print still works off the picked building.
  const [campus, setCampus] = useState(false);
  const [campusLoad, setCampusLoad] = useState({ done: 0, total: 0 });
  const [zoom, setZoom] = useState(17);
  const [bounds, setBounds] = useState(null);
  const [slug, setSlug] = useState('');
  const [floor, setFloor] = useState(1);
  const [selNode, setSelNode] = useState('');
  const [showRoomNumbers, setShowRoomNumbers] = useState(false);
  const [perPage, setPerPage] = useState(1);

  // Remembered, and seeded from the machine's own setting the first time —
  // somebody who runs everything light should not have to switch this tool
  // every morning to find out it remembers.
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem(THEME_KEY);
      if (saved === 'light' || saved === 'dark') return saved;
      return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    } catch { return 'dark'; }
  });
  useEffect(() => { try { localStorage.setItem(THEME_KEY, theme); } catch { /* full */ } }, [theme]);
  const themed = theme === 'light' ? ' light' : '';

  // The sign set doubles as the session check: a stale token fails here and
  // drops straight back to the login card rather than to an empty tool.
  useEffect(() => {
    if (!token) { setSigns([]); setUser(''); return; }
    let dead = false;
    api().then(d => {
      if (dead) return;
      setSigns(d.signs || []);
      setUser(d.user || '');
      // The scope from the READ is authoritative — the server resolves it fresh,
      // where the one stamped into the token at login is only a first guess.
      setScope(d.buildings ?? '*');
      setIsAdmin(!!d.admin);
    }).catch(e => { if (!dead) setErr(e.message); });
    return () => { dead = true; };
  }, [token, api]);

  useEffect(() => {
    if (!token) return;
    let dead = false;
    snapshotJson('manifest.json')
      .then(d => {
        if (dead) return;
        const list = d?.buildings || [];
        setManifest(list);
        if (!list.length) setErr('The campus map data did not load — the building list is empty.');
      })
      .catch(() => { if (!dead) setErr('Could not load the campus map data.'); });
    return () => { dead = true; };
  }, [token]);

  // ⚠️ `tunnels` is filtered out of BOTH lists even though it is no longer in
  // PSEUDO. It is not a building anybody administers — there is no faculty to
  // scope it to, and offering it in the Accounts screen's checklist would be
  // offering a faculty something nobody should hold. It is reached only through
  // the admin campus view.
  const allBuildings = useMemo(() => manifest
    .filter(b => !PSEUDO.has(b.slug) && b.slug !== TUNNELS)
    .sort((a, b) => (a.name || a.slug).localeCompare(b.name || b.slug)),
  [manifest]);

  const buildings = useMemo(() => manifest
    .filter(b => !PSEUDO.has(b.slug) && b.slug !== TUNNELS)
    .filter(b => scope === '*' || (Array.isArray(scope) && scope.includes(b.slug)))
    .sort((a, b) => (a.name || a.slug).localeCompare(b.name || b.slug)),
  [manifest, scope]);

  const nameOf = useCallback(
    (s) => manifest.find(b => b.slug === s)?.name || prettifySlug(s), [manifest]);

  // Per-building geometry, fetched once and kept: switching floors, views or
  // back to a building already looked at should not re-download it.
  const geoRef = useRef(new Map());                // slug → { nav, struct }
  const [geoTick, setGeoTick] = useState(0);
  useEffect(() => {
    if (!slug || geoRef.current.has(slug)) return;
    let dead = false;
    geoRef.current.set(slug, null);                // in flight
    setStatus(`Loading ${nameOf(slug)}…`);
    Promise.all([
      snapshotJson(`nav_${encodeURIComponent(slug)}.json`).catch(() => null),
      snapshotJson(`${encodeURIComponent(slug)}.json`).catch(() => null),
    ]).then(([nav, struct]) => {
      if (dead) return;
      geoRef.current.set(slug, { nav, struct });
      setGeoTick(t => t + 1);
      setStatus(nav ? '' : 'That building has no nav graph — nothing to place a sign on.');
    });
    return () => { dead = true; };
  }, [slug, nameOf]);

  // Re-read on geoTick: the fetch above stores into a ref, so the tick is what
  // tells React the geometry has landed.
  const geo = (slug && geoRef.current.get(slug)) || null;

  // ── The campus view's geometry ─────────────────────────────────────────────
  //
  // Loaded into the SAME `geoRef` the single-building view fills, so the two
  // warm each other: opening the campus view and then a building costs one
  // fetch, not two. Sequential rather than 40 parallel requests — this is a
  // background fill behind a usable map, not something anybody waits on, and 40
  // simultaneous 200 KB fetches would stall the tunnel layer that IS wanted
  // immediately.
  const [campusTick, setCampusTick] = useState(0);
  const outlinesRef = useRef(null);
  useEffect(() => {
    if (!campus || !manifest.length) return;
    let dead = false;
    const slugs = [TUNNELS, ...manifest
      .filter(b => !PSEUDO.has(b.slug) && b.slug !== TUNNELS)
      .map(b => b.slug)];

    (async () => {
      // Footprints first and cheaply (160 KB for all 40): they are what makes a
      // tunnel junction legible as "under University Centre" rather than a dot
      // on grass, and they land long before the nav files behind them.
      if (!outlinesRef.current) {
        outlinesRef.current = await snapshotJson('outlines.json').catch(() => null);
        if (!dead) setCampusTick(t => t + 1);
      }
      setCampusLoad({ done: 0, total: slugs.length });
      let done = 0;
      for (const sl of slugs) {
        if (dead) return;
        if (!geoRef.current.get(sl)) {
          const [nav, struct] = await Promise.all([
            snapshotJson(`nav_${encodeURIComponent(sl)}.json`).catch(() => null),
            // Only the tunnels' own polygons are wanted here. A building's
            // rooms are 4 MB across campus and are never drawn in this view.
            sl === TUNNELS ? snapshotJson(`${TUNNELS}.json`).catch(() => null) : Promise.resolve(null),
          ]);
          if (dead) return;
          geoRef.current.set(sl, { nav, struct });
        }
        done++;
        // Ticked per file so the tunnels — first in the list — are clickable
        // while the rest are still arriving.
        setCampusLoad({ done, total: slugs.length });
        setCampusTick(t => t + 1);
      }
    })();
    return () => { dead = true; };
  }, [campus, manifest]);

  /**
   * Every node the campus view can place a sign on, tagged with its owner.
   *
   * ⚠️ THE TAG IS LOAD-BEARING. A building's nav nodes carry `floor` but NOT a
   * `building` field — only the tunnels' do — so without `__slug` a sign placed
   * from this view would be attributed to whatever building happened to be
   * selected, saved into that building's set, and printed with its name on it.
   * Verified against the snapshot: 0 of 40 building nav files tag their nodes.
   */
  const campusNodes = useMemo(() => {
    if (!campus) return [];
    const out = [];
    for (const b of manifest) {
      if (PSEUDO.has(b.slug)) continue;
      const nav = geoRef.current.get(b.slug)?.nav;
      if (!nav) continue;
      const wanted = b.slug === TUNNELS ? null : CAMPUS_FLOOR;
      for (const n of nav.nodes || []) {
        if (!Number.isFinite(n.lat)) continue;
        if (wanted != null && (n.floor ?? 1) !== wanted) continue;
        if (n.type === 'door') continue;   // same rule as the single-floor view
        out.push({ ...n, __slug: b.slug, __name: b.name || prettifySlug(b.slug) });
      }
    }
    return out;
  // campusTick is what says the ref has more in it — the fetch above stores
  // into a ref, which React cannot see.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campus, manifest, campusTick]);

  /**
   * What actually gets a marker: the tunnels always, the buildings only when
   * the map is close enough to tell one dot from another, and only inside the
   * viewport.
   *
   * The cap is a backstop, not the mechanism — at CAMPUS_NODE_ZOOM a viewport
   * holds a building or two. It exists so that a bad `bounds` (a map still
   * settling, a window resize mid-pan) degrades into a slow frame rather than
   * four thousand markers.
   */
  const campusVisible = useMemo(() => {
    if (!campus) return [];
    const tunnels = campusNodes.filter(n => n.__slug === TUNNELS);
    if (zoom < CAMPUS_NODE_ZOOM) return tunnels;
    const inView = (n) => !bounds || bounds.contains({ lat: n.lat, lng: n.lng });
    const rest = campusNodes.filter(n => n.__slug !== TUNNELS && inView(n));
    return [...tunnels, ...rest.slice(0, CAMPUS_MARKER_CAP)];
  }, [campus, campusNodes, zoom, bounds]);

  const floors = useMemo(() => {
    const set = new Set((geo?.nav?.nodes || []).map(n => n.floor ?? 1));
    return [...set].sort((a, b) => a - b);
  }, [geo]);

  useEffect(() => {
    if (!floors.length) return;
    if (!floors.includes(floor)) setFloor(floors.includes(1) ? 1 : floors[0]);
  }, [floors, floor]);

  // Everything on the floor, doors included. Not what gets drawn — it is what a
  // room polygon resolves against, and what a placed sign is looked up in.
  const allFloorNodes = useMemo(
    () => (geo?.nav?.nodes || []).filter(n =>
      (n.floor ?? 1) === floor && Number.isFinite(n.lat)),
    [geo, floor]);

  // Door nodes are excluded from the dots. There is one per room, they
  // outnumber everything else on a floor several times over, and dropping them
  // is what makes the places a sign actually goes visible rather than buried in
  // a field of identical points. Rooms are reachable by clicking their POLYGON
  // instead — a target the size of the room, which is the honest shape of
  // "put a sign at 154" anyway.
  const floorNodes = useMemo(() => allFloorNodes.filter(n => n.type !== 'door'), [allFloorNodes]);

  // room_number → the node that serves it, preferring the door. Both the plan
  // polygons and the nav nodes carry the number, so this is the join between
  // them; the ring test below only has to cover what it misses.
  const nodeForRoom = useMemo(() => {
    const m = new Map();
    for (const n of allFloorNodes) {
      const key = String(n.room_number || '').trim();
      if (!key) continue;
      const prev = m.get(key);
      if (!prev || (prev.type !== 'door' && n.type === 'door')) m.set(key, n);
    }
    return m;
  }, [allFloorNodes]);

  /** The node a room polygon stands for, or null if nothing serves it. */
  const nodeForPolygon = useCallback((p) => {
    const num = String(p.room_number || '').trim();
    const byNumber = num && nodeForRoom.get(num);
    if (byNumber) return byNumber;
    // A polygon with no number — a stairwell, a lift, a room the survey left
    // unlabelled — is matched geometrically instead. The door wins if one is in
    // there; otherwise whatever is (the stair node inside a stair polygon).
    let fallback = null;
    for (const n of allFloorNodes) {
      if (!pointInRing(n, p.coordinates || [])) continue;
      if (n.type === 'door') return n;
      if (!fallback) fallback = n;
    }
    return fallback;
  }, [nodeForRoom, allFloorNodes]);

  const signIds = useMemo(() => new Set(signs.map(s => s.nodeId)), [signs]);

  const countBySlug = useMemo(() => {
    const m = new Map();
    for (const s of signs) m.set(s.building, (m.get(s.building) || 0) + 1);
    return m;
  }, [signs]);

  // ── Editing ────────────────────────────────────────────────────────────────
  // Every mutation is computed from a ref, not from render state: placing signs
  // is a run of rapid clicks, and two in the same tick would otherwise both read
  // the pre-click list and mint the same identifier.
  const signsRef = useRef(signs);
  useEffect(() => { signsRef.current = signs; }, [signs]);
  const savedIdsRef = useRef(new Set());
  useEffect(() => { if (!dirty) savedIdsRef.current = new Set(signs.map(s => s.id)); }, [signs, dirty]);

  // Which buildings differ from what was last saved — a placement in one, or a
  // removal from one. Derived rather than tracked in state: a `Set` updated on
  // every click is one more thing that can fall out of step with the signs it
  // describes, and the answer is cheap to compute at the moment it is needed.
  const savedSnapRef = useRef([]);
  useEffect(() => { if (!dirty) savedSnapRef.current = signs; }, [signs, dirty]);
  const dirtyBuildingsRef = useRef(() => []);
  dirtyBuildingsRef.current = () => {
    const now = new Map(), was = new Map();
    for (const s of signsRef.current) now.set(s.building, (now.get(s.building) || 0) + 1);
    for (const s of savedSnapRef.current) was.set(s.building, (was.get(s.building) || 0) + 1);
    const ids = new Set(savedSnapRef.current.map(s => s.id));
    const changed = new Set();
    for (const s of signsRef.current) if (!ids.has(s.id)) changed.add(s.building);
    // A removal leaves no row to notice, so the counts are compared too.
    for (const [b, n] of was) if ((now.get(b) || 0) !== n) changed.add(b);
    return [...changed];
  };

  const mutate = useCallback((fn) => {
    const next = fn(signsRef.current);
    signsRef.current = next;
    setSigns(next);
    setDirty(true);
  }, []);

  /** Clicking a node — or the room polygon that stands for one — places a sign
   *  there; that is the whole tool. Clicking one that already carries a sign
   *  removes it: the map is where you see what is placed, so it is where you
   *  unplace it.
   *
   *  `where` is the room the click came through, when it came through one. It
   *  fills the sign's `location`, which until now was always blank because
   *  nothing set it — it is what the printed placement guide prints beside the
   *  identifier, and "Room 154" is the sentence somebody walking the corridor
   *  with a stack of signs actually needs. */
  const placeSign = useCallback((node, where = '') => {
    setSelNode(node.id);
    const existing = signsRef.current.find(s => s.nodeId === node.id);
    if (existing) {
      if (savedIdsRef.current.has(existing.id) &&
          !window.confirm(`Remove ${existing.code}? It has been saved, so any sheet already printed with that identifier is out of date.`)) return;
      mutate(list => list.filter(s => s.id !== existing.id));
      setStatus(`Removed ${existing.code} — click ${where || 'the node'} again to place a new sign.`);
      return;
    }
    // ⚠️ THE NODE OWNS THE ATTRIBUTION, not the selected building. In the
    // campus view a click can land on any of 40 buildings or on the tunnels,
    // and `slug`/`floor` describe whichever building the sidebar happens to be
    // pointing at — which is usually not the one under the cursor. A sign filed
    // against the wrong building is saved into the wrong set, printed with the
    // wrong name on it, and counted on the wrong row.
    const owner = node.__slug || slug;
    const lvl = node.__slug ? (node.floor ?? CAMPUS_FLOOR) : floor;
    const ownerName = node.__name || nameOf(owner);
    const code = suggestCode(owner, lvl, signsRef.current);
    mutate(list => [...list, makeSign({
      slug: owner, floor: lvl, nodeId: node.id, code,
      label: `${ownerName} — ${where || nodeName(node)}`,
      location: where,
    })]);
    // Deliberately stays on this pane: placing signs is a run of clicks, and
    // being thrown to the directory after each one would break the rhythm.
    // The building is named in the campus view, where it is the thing you can
    // most easily be wrong about.
    setStatus(node.__slug
      ? `Added ${code} in ${ownerName} — keep clicking to add more.`
      : `Added ${code} at ${where || nodeName(node)} — keep clicking to add more.`);
  }, [slug, floor, nameOf, mutate]);

  /**
   * Write the placed signs back.
   *
   * ⚠️ EVERY TOUCHED BUILDING, not just the selected one. The POST replaces one
   * building's whole set, and a campus-view session legitimately places signs
   * in the tunnels and in three buildings at once — saving only `slug` would
   * silently drop the rest, with the screen still showing them as placed. The
   * set is computed by diffing against what was last saved, so an untouched
   * building is never rewritten.
   *
   * Sequential, because each POST is a read-modify-write of ONE campus-wide
   * document: firing them in parallel means the last response wins and the
   * others are lost.
   */
  const save = useCallback(async () => {
    const touched = campus ? dirtyBuildingsRef.current() : (slug ? [slug] : []);
    if (!touched.length) return;
    setSaving(true); setErr(''); setStatus('Saving…');
    let saved = 0;
    try {
      for (const b of touched) {
        const mine = signsRef.current.filter(s => s.building === b);
        const d = await api('', { building: b, signs: mine });
        // The server merges into the campus-wide document and hands back this
        // building's saved rows — including any `scans` it preserved — so the
        // screen ends up showing what is stored rather than what was sent.
        const others = signsRef.current.filter(s => s.building !== b);
        const next = [...others, ...(d.signs || [])];
        signsRef.current = next;
        setSigns(next);
        saved += mine.length;
      }
      savedIdsRef.current = new Set(signsRef.current.map(s => s.id));
      setDirty(false);
      setStatus(touched.length === 1
        ? `Saved ${saved} sign(s) in ${nameOf(touched[0])}.`
        : `Saved ${saved} sign(s) across ${touched.length} buildings.`);
    } catch (e) {
      // Partial progress is kept and said out loud: some buildings are written
      // and some are not, and "it failed" would read as nothing having changed.
      setStatus(`${e.message} — ${saved} sign(s) were saved before that.`);
    }
    setSaving(false);
  }, [api, slug, nameOf, campus]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // ── The map ────────────────────────────────────────────────────────────────
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: MAPS_KEY || '' });
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const polysRef = useRef([]);                     // [{ p, base, nodeId, hover }]
  const linesRef = useRef([]);
  const labelsRef = useRef([]);                    // room-number labels
  const markersRef = useRef([]);                   // [{ m, id }]
  // Assigned by the restyle effect and called from the polygon hover handlers,
  // so hovering a room re-reads the current sign/selection state instead of the
  // stale copy the build effect closed over.
  const polyStyleRef = useRef(() => {});
  const fittedRef = useRef('');
  // Read inside the marker click handlers, so placing a sign never has to tear
  // down and rebuild the floor's markers.
  const placeRef = useRef(placeSign);
  useEffect(() => { placeRef.current = placeSign; }, [placeSign]);

  // The map div only exists once a building is picked, and `slug` was not a
  // dependency of the effect that created the map — so on a first launch the
  // effect ran while the div was still absent and never ran again. The map
  // appeared only after something else in the dependency list changed, which is
  // why switching to another tab and back "fixed" it.
  //
  // A callback ref removes the dependency list from the question entirely: it
  // fires when the div actually mounts, and again with null when it goes away.
  // `divTick` re-runs the effect for the other order — div first, Maps script
  // still loading — which a ref alone cannot catch.
  const [divTick, setDivTick] = useState(0);
  const attachMapDiv = useCallback((node) => {
    mapDivRef.current = node;
    if (!node) {
      // The div React gave Google has gone; the instance must go with it or the
      // next mount draws onto a detached node.
      mapRef.current = null;
      setMapReady(false);
      fittedRef.current = '';
    }
    setDivTick(t => t + 1);
  }, []);

  useEffect(() => {
    if (!isLoaded || !mapDivRef.current || mapRef.current) return;
    mapRef.current = new window.google.maps.Map(mapDivRef.current, {
      center: CENTER, zoom: 16, mapTypeId: 'satellite',
      tilt: 0, heading: 0, rotateControl: false,
      mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      clickableIcons: false, gestureHandling: 'greedy',
    });
    setMapReady(true);
  }, [isLoaded, divTick]);

  // Structure polygons + nav edges for the floor: context, not targets.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    const clear = () => {
      polysRef.current.forEach(o => o.p.setMap(null)); polysRef.current = [];
      linesRef.current.forEach(o => o.setMap(null)); linesRef.current = [];
    };
    clear();
    if (!geo?.struct && !geo?.nav) return;

    const polys = (geo?.struct?.polygons || []);
    const outlines = polys.filter(p => p.type === 'outline');
    // A floor with no outline of its own borrows the lowest one, so an upper
    // floor still sits on a visible footprint instead of floating.
    let footprint = outlines.filter(p => p.floor === floor);
    if (!footprint.length && outlines.length) {
      const lowest = Math.min(...outlines.map(o => o.floor));
      footprint = outlines.filter(o => o.floor === lowest);
    }
    const ordered = [...footprint, ...polys.filter(p => p.type !== 'outline' && p.floor === floor)];
    for (const p of ordered) {
      const st = POLY_OPTS[p.type] || POLY_OPTS.room;
      // The outline is the whole footprint and `blocked` is not a place — either
      // one taking clicks would swallow every miss on the floor beneath it.
      const node = p.type === 'outline' || p.type === 'blocked' ? null : nodeForPolygon(p);
      const poly = new window.google.maps.Polygon({
        map, clickable: !!node, zIndex: p.type === 'outline' ? 1 : 2,
        paths: (p.coordinates || []).map(c => ({ lat: c.latitude, lng: c.longitude })),
        fillColor: st.fill, fillOpacity: st.op,
        strokeColor: st.stroke, strokeOpacity: 1, strokeWeight: st.w,
      });
      const o = { p: poly, base: st, nodeId: node?.id || '', hover: false };
      if (node) {
        // Only a numbered room names itself. An unlabelled polygon — a stair
        // shaft, a survey gap — falls through to the node's own name, exactly
        // as clicking its dot would, rather than inventing a location for the
        // printed guide out of an internal id.
        const where = p.room_number ? `Room ${p.room_number}` : '';
        poly.addListener('click', () => placeRef.current(node, where));
        // Google gives a polygon no cursor of its own, so without this a room is
        // clickable with nothing on screen saying so.
        poly.addListener('mouseover', () => { o.hover = true; polyStyleRef.current(o); });
        poly.addListener('mouseout', () => { o.hover = false; polyStyleRef.current(o); });
      }
      polysRef.current.push(o);
    }

    const byId = new Map(floorNodes.map(n => [n.id, n]));
    for (const e of geo?.nav?.edges || []) {
      const a = byId.get(e.from), b = byId.get(e.to);
      if (!a || !b) continue;
      linesRef.current.push(new window.google.maps.Polyline({
        map, clickable: false, zIndex: 200,
        strokeColor: '#8fa8bd', strokeOpacity: 0.65, strokeWeight: 2,
        path: [{ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng }],
      }));
    }
    return clear;
  }, [mapReady, geo, floor, floorNodes, geoTick, nodeForPolygon]);

  // Room numbers, drawn as label-only markers over each room's centroid.
  //
  // Their own effect, and off by default: a floor carries hundreds of them, they
  // are context rather than anything to click, and switching them on and off must
  // not rebuild the polygons or the nodes underneath.
  useEffect(() => {
    if (!mapReady) return;
    const clear = () => { labelsRef.current.forEach(o => o.setMap(null)); labelsRef.current = []; };
    clear();
    if (!showRoomNumbers) return;
    for (const p of (geo?.struct?.polygons || [])) {
      if (p.floor !== floor || !p.room_number) continue;
      if (p.type === 'outline' || p.type === 'blocked') continue;
      const ring = p.coordinates || [];
      if (!ring.length) continue;
      let lat = 0, lng = 0;
      for (const c of ring) { lat += c.latitude; lng += c.longitude; }
      labelsRef.current.push(new window.google.maps.Marker({
        map: mapRef.current, clickable: false, zIndex: 250,
        position: { lat: lat / ring.length, lng: lng / ring.length },
        // A zero-size symbol: the marker exists only to carry the label, and a
        // default pin under every room number would bury the floor.
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 0, fillOpacity: 0, strokeOpacity: 0 },
        // Black, not white. The room fills are near-white, so white numerals were
        // invisible on exactly the polygons they label — legible only where they
        // happened to spill onto the satellite image around the building.
        label: { text: String(p.room_number), color: '#111418', fontSize: '11px', fontWeight: '700' },
      }));
    }
    return clear;
  }, [mapReady, geo, floor, showRoomNumbers, geoTick]);

  // Frame the tunnel network when the campus view opens: it is the subject, it
  // is 25 nodes, and fitting to all 3,698 would frame the whole city block and
  // show the reader nothing.
  useEffect(() => {
    if (!mapReady || !campus) return;
    const t = campusNodes.filter(n => n.__slug === TUNNELS);
    if (!t.length || fittedRef.current === '__campus') return;
    const b = new window.google.maps.LatLngBounds();
    t.forEach(n => b.extend({ lat: n.lat, lng: n.lng }));
    mapRef.current.fitBounds(b, 60);
    fittedRef.current = '__campus';
  }, [mapReady, campus, campusNodes]);

  // Frame a newly picked building once its geometry lands.
  useEffect(() => {
    if (campus) return;
    if (!mapReady || !slug || fittedRef.current === slug || !floorNodes.length) return;
    const b = new window.google.maps.LatLngBounds();
    floorNodes.forEach(n => b.extend({ lat: n.lat, lng: n.lng }));
    mapRef.current.fitBounds(b, 40);
    fittedRef.current = slug;
  }, [mapReady, slug, floorNodes, campus]);

  // Markers are built once per floor — a floor can carry 300 — and restyled
  // below when a sign is placed, rather than rebuilt.
  const drawNodes = campus ? campusVisible : floorNodes;
  useEffect(() => {
    if (!mapReady) return;
    const clear = () => { markersRef.current.forEach(o => o.m.setMap(null)); markersRef.current = []; };
    clear();
    for (const n of drawNodes) {
      const m = new window.google.maps.Marker({
        map: mapRef.current, position: { lat: n.lat, lng: n.lng },
        // Tunnels ride above the building nodes they pass under. In this view
        // they are the subject, and at a junction the two sit on top of one
        // another — the tunnel one is the one being aimed at.
        zIndex: n.__slug === TUNNELS ? 340 : 300,
        // The building is in the tooltip because in the campus view it is the
        // thing you cannot read off the dot.
        title: n.__slug && n.__slug !== TUNNELS ? `${n.__name} — ${nodeName(n)}` : nodeName(n),
      });
      m.addListener('click', () => placeRef.current(n));
      markersRef.current.push({ m, id: n.id, type: n.type, tunnel: n.__slug === TUNNELS });
    }
    return clear;
  }, [mapReady, drawNodes, campus]);

  // ── The tunnel runs ────────────────────────────────────────────────────────
  // 25 dots do not show a network; the 92 edges between them do, and "which
  // junction is this" is unanswerable without the corridors meeting at it. Drawn
  // once, and never rebuilt on a pan — the geometry does not change.
  const tunnelLinesRef = useRef([]);
  useEffect(() => {
    if (!mapReady) return;
    const clear = () => { tunnelLinesRef.current.forEach(l => l.setMap(null)); tunnelLinesRef.current = []; };
    clear();
    if (!campus) return;
    const nav = geoRef.current.get(TUNNELS)?.nav;
    if (!nav) return;

    // ⚠️ ENDPOINTS ARE RESOLVED ACROSS THE WHOLE CAMPUS, not just within the
    // tunnels file. Measured: only 16 of the 92 tunnel edges join two tunnel
    // nodes — the other 76 run from a tunnel waypoint into a BUILDING, which is
    // exactly what a tunnel is for. Looking them up in nav_tunnels alone drew
    // 16 disconnected stubs and read as a network that barely exists, with
    // every entrance missing. Every floor is indexed, not just floor 1: a
    // tunnel meets a building at whatever level its basement door is on.
    const at = new Map();
    for (const b of manifest) {
      if (PSEUDO.has(b.slug)) continue;
      for (const n of geoRef.current.get(b.slug)?.nav?.nodes || []) {
        if (Number.isFinite(n.lat) && !at.has(n.id)) at.set(n.id, n);
      }
    }

    for (const e of nav.edges || []) {
      const a = at.get(e.from);
      const b = at.get(e.to);
      if (!a || !b || !Number.isFinite(a.lat) || !Number.isFinite(b.lat)) continue;
      tunnelLinesRef.current.push(new window.google.maps.Polyline({
        map: mapRef.current, clickable: false, zIndex: 120,
        path: [{ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng }],
        strokeColor: TUNNEL_COLOR, strokeOpacity: 0.95, strokeWeight: 4,
      }));
    }
    return clear;
  }, [mapReady, campus, campusTick, manifest]);

  // ── Building footprints, as context ────────────────────────────────────────
  // What turns a dot under grass into "the junction under University Centre".
  // One outline per building, not per floor: outlines.json carries one per floor
  // and a six-storey building would otherwise be six stacked copies.
  const footRef = useRef([]);
  useEffect(() => {
    if (!mapReady) return;
    const clear = () => { footRef.current.forEach(o => o.setMap(null)); footRef.current = []; };
    clear();
    if (!campus || !outlinesRef.current) return;
    for (const b of outlinesRef.current.buildings || []) {
      if (PSEUDO.has(b.slug) || b.slug === TUNNELS) continue;
      const ring = (b.polygons || []).find(x => x.type === 'outline')?.coordinates || [];
      if (ring.length < 3) continue;
      footRef.current.push(new window.google.maps.Polygon({
        map: mapRef.current, clickable: false, zIndex: 20,
        paths: ring.map(c => ({ lat: c.latitude, lng: c.longitude })),
        fillColor: '#ffffff', fillOpacity: 0.30,
        strokeColor: '#ffffff', strokeOpacity: 0.55, strokeWeight: 1,
      }));
    }
    return clear;
  }, [mapReady, campus, campusTick]);

  // Zoom and viewport, which is what the marker cull reads. `idle` rather than
  // `bounds_changed`: the latter fires on every frame of a pan and would
  // rebuild several hundred markers per frame.
  useEffect(() => {
    if (!mapReady || !campus) return;
    const m = mapRef.current;
    const read = () => { setZoom(m.getZoom() ?? 17); setBounds(m.getBounds() || null); };
    read();
    const l = m.addListener('idle', read);
    return () => l.remove();
  }, [mapReady, campus]);

  // No marker is drawn on a room's door, even when it carries a sign. The
  // filled room IS the mark: a door node is a speck against the shape around
  // it, and putting a dot on top only says the same thing twice, in the layer
  // that was hidden precisely because there are hundreds of them. The room
  // stays clickable, so it is still where the sign comes off again.
  useEffect(() => {
    if (!mapReady) return;
    for (const o of markersRef.current) {
      const isSign = signIds.has(o.id);
      const sel = o.id === selNode;
      // The fill says what it is, the ring says where you are. Keeping the two
      // on separate channels means a selected sign still reads as a sign.
      o.m.setIcon({
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: isSign ? 8 : (o.tunnel ? 6.5 : 5),
        // A tunnel node keeps its own colour until it carries a sign, at which
        // point it becomes a sign like any other: "has a sign" is the state the
        // whole screen is read for, and a second colour for it in the tunnels
        // would be a second thing to learn for no gain.
        fillColor: isSign ? SIGN_COLOR : (o.tunnel ? TUNNEL_COLOR : NODE_COLOR),
        fillOpacity: 1,
        strokeColor: sel ? SEL_COLOR : '#ffffff',
        strokeWeight: sel ? 3 : 1.4,
      });
      o.m.setZIndex(isSign ? 320 : 300);
    }

    // Rooms take the same states, in fill and outline rather than in a dot: a
    // room carrying a sign has to be readable at a glance while walking the
    // floor, and no marker is drawn on its door.
    const style = (o) => {
      if (!o.nodeId) return;
      const isSign = signIds.has(o.nodeId);
      const sel = o.nodeId === selNode;
      o.p.setOptions({
        fillColor: isSign ? SIGN_COLOR : o.base.fill,
        fillOpacity: isSign ? 0.55 : o.base.op,
        strokeColor: sel ? SEL_COLOR : (o.hover ? '#111418' : o.base.stroke),
        strokeWeight: sel || o.hover ? o.base.w + 1.5 : o.base.w,
        zIndex: isSign || sel || o.hover ? 3 : 2,
      });
    };
    polyStyleRef.current = style;
    polysRef.current.forEach(style);
  }, [mapReady, signIds, selNode, floorNodes, geo, floor, geoTick]);

  // ── Derived lists ──────────────────────────────────────────────────────────

  const buildingCount = useMemo(
    () => signs.filter(s => s.building === slug).length, [signs, slug]);

  const toPrint = useMemo(
    () => signs.filter(s => s.building === slug && s.enabled).sort((a, b) => a.code.localeCompare(b.code)),
    [signs, slug]);

  // Duplicate identifiers make two signs indistinguishable in the scan report
  // and put the same code on two walls. Surfaced rather than silently allowed —
  // the server refuses them, so a save would otherwise fail with no clue why.

  /** The floors the guide will cover, with a straightened plan for each. */
  const guideFloors = useMemo(() => {
    if (!geo?.nav) return [];
    const used = [...new Set(toPrint.map(s => s.floor))].sort((a, b) => a - b);
    return used
      .map(floorNum => ({ floorNum, plan: planFloor(geo.nav, geo.struct, floorNum) }))
      .filter(f => f.plan);
  }, [geo, toPrint]);

  /**
   * Print the sheet from a hidden iframe, without opening a window.
   *
   * The tab this replaces was written into with document.write() on a window
   * opened at about:blank — and about:blank is still loading when the write
   * lands. When that navigation finally commits, a few seconds later, it
   * replaces the document underneath, which is the pages going blank while you
   * look at them. Writing into an iframe has no such race: the frame is already
   * ours, its document is complete before anything is printed, and there is no
   * second navigation waiting to land on top.
   *
   * It also puts the print dialog one click away instead of two, survives a
   * pop-up blocker, and leaves nothing for the user to close afterwards.
   *
   * ⚠️ The mark is read FIRST and handed to the sheet as a data URI, so it is
   * part of the SVG rather than an `<img>` racing the print dialog. print()
   * captures the document as it stands; losing that race printed a hole where
   * the badge goes, on a whole run of signs, with nothing to show for it.
   * `loadBadgeDataUrl()` never rejects — a null falls back to the overlay, and
   * the wait below is what covers that case. The 3s cap is a backstop: a badge
   * that will not load must not mean a button that does nothing.
   */
  const printSheet = useCallback(async () => {
    if (!toPrint.length) return;
    const html = buildSignSheet({
      slug, building: nameOf(slug), signs: toPrint, floors: guideFloors,
      base: DEFAULT_BASE, perPage,
      logoUrl: `${window.location.origin}${LOGO_URL}`,
      logoDataUrl: await loadBadgeDataUrl(),
    });

    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('title', 'Print sheet');
    // Off-screen rather than display:none — a frame with no box does not lay
    // out, and a document that never laid out has nothing to paginate.
    frame.style.cssText = 'position:fixed; left:-10000px; top:0; width:1px; height:1px; border:0;';
    document.body.appendChild(frame);

    const win = frame.contentWindow;
    if (!win) {   // no frame, no print: fall back to the tab rather than fail silently
      frame.remove();
      const w = window.open('', '_blank');
      if (!w) { setStatus('The print window was blocked — allow pop-ups for this site.'); return; }
      w.document.write(html); w.document.close();
      // Wired from here, not with an onclick in the sheet: the new tab
      // inherits the site's CSP, which blocks inline event handlers.
      w.document.querySelector('[data-print]')?.addEventListener('click', () => w.print());
      return;
    }

    win.document.open();
    win.document.write(html);
    win.document.close();

    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      try { win.focus(); win.print(); } catch { setStatus('The browser would not open the print dialog.'); }
      // Only after the dialog closes — tearing the frame down while it is open
      // cancels the print. The timeout covers browsers that never fire it.
      const drop = () => frame.remove();
      win.addEventListener?.('afterprint', drop, { once: true });
      setTimeout(drop, 60_000);
    };

    const images = [...(win.document.images || [])];
    Promise.all(images.map(img => (img.complete ? null : new Promise(res => {
      img.addEventListener('load', res, { once: true });
      img.addEventListener('error', res, { once: true });
    })))).then(go);
    setTimeout(go, 3000);
  }, [toPrint, guideFloors, slug, nameOf, perPage]);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (!MAPS_KEY) {
    return <div className={`bd-login${themed}`}><style>{CSS}</style>
      <div className="bd-card">Missing <code>REACT_APP_MAPS_KEY</code>.</div></div>;
  }

  if (!token) {
    return (
      <div className={`bd-login${themed}`}>
        <style>{CSS}</style>
        <form className="bd-card" onSubmit={login}>
          <UMapsWordmark tool="Building Directories" size={28} href={null}
                         style={{ marginBottom: 2 }} />
          <p>Generate the printed QR signage for your buildings.</p>
          <input className="bd-input" autoFocus placeholder="Faculty or editor account" value={username}
                 onChange={e => setUsername(e.target.value)} autoComplete="username" />
          <input className="bd-input" type="password" placeholder="Password" value={pw}
                 onChange={e => setPw(e.target.value)} autoComplete="current-password" />
          <button className="bd-btn bd-primary" disabled={busy || !username || !pw}>
            {busy ? 'Checking…' : 'Sign in'}
          </button>
          <p className="bd-note">Your Tour Creator editor account, or the UMaps admin login, works here too.</p>
          {err && <div className="bd-err">{err}</div>}
        </form>
      </div>
    );
  }

  if (loadError) {
    return <div className={`bd-login${themed}`}><style>{CSS}</style>
      <div className="bd-card">Google Maps failed to load.</div></div>;
  }

  return (
    <div className={`bd-root${themed}`}>
      <style>{CSS}</style>

      <header className="bd-top">
        <UMapsWordmark tool="Building Directories" />
        <span className="bd-who">{user}{scope !== '*' && Array.isArray(scope)
          ? ` · ${scope.length} building${scope.length === 1 ? '' : 's'}` : ' · all buildings'}</span>
        {/* ⚠️ `!slug` alone would disable this in the campus view, where there
            is no picked building and signs have just been placed in five. */}
        <button className={`bd-btn ${dirty ? 'bd-primary' : ''}`} onClick={save}
                disabled={!dirty || saving || (!slug && !campus)}>
          {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
        </button>
        {/* Campus-wide, so it belongs beside the account name rather than in the
            per-building pane row. Only the operator sees it — a faculty must not
            be able to read the list of every other account, let alone widen its
            own scope. */}
        {isAdmin && (
          <button className={`bd-btn ${page === 'accounts' ? 'bd-primary' : ''}`}
                  onClick={() => setPage(page === 'accounts' ? 'signs' : 'accounts')}>
            {page === 'accounts' ? 'Back to signs' : 'Accounts'}
          </button>
        )}
        <button className="bd-btn" onClick={() => {
          if (dirty && !window.confirm('You have unsaved signs. Sign out anyway?')) return;
          saveToken('');
        }}>Sign out</button>
        {/* An error from a request the user did not make (the sign set failing
            to load, say) has to land somewhere they will see it — otherwise the
            tool just looks empty and there is nothing to act on. Only a 401
            drops back to the login card, where `err` shows instead. */}
        <div className="bd-topright">
          {(err || status) && (
            <span className={`bd-status ${err ? 'bd-status-err' : ''}`}>{err || status}</span>
          )}
          <ThemeSwitch theme={theme} onChange={setTheme} />
        </div>
      </header>

      {page === 'accounts' && (
        <AccountManager token={token} buildings={allBuildings} onStatus={setStatus} onError={setErr} />
      )}

      <div className="bd-body" hidden={page === 'accounts'}>
        <aside className="bd-side">
          {/* No filter box. An account is scoped to the buildings it looks
              after — a handful, not the campus — so the list is already short
              enough to read, and a search field above it was furniture. */}
          <div className="bd-blist">
            {/* ⚠️ ADMIN ONLY, and above the buildings rather than among them:
                the tunnels are not a building somebody administers, and a row
                in that list would read as one more place a faculty could be
                given. It is a different WAY OF LOOKING — the whole campus at
                once — which is what placing signs along a run that crosses
                fifteen buildings actually needs. */}
            {isAdmin && (
              <button className={`bd-brow bd-campus ${campus ? 'on' : ''}`}
                      onClick={() => {
                        if (dirty && !campus &&
                            !window.confirm('You have unsaved signs. Switch to the campus view anyway?')) return;
                        setCampus(v => !v); setSelNode(''); setView('place');
                        fittedRef.current = '';
                      }}>
                <span className="bd-brow-name">
                  Tunnels &amp; campus
                  <em>{campus ? 'showing every first floor' : 'the whole tunnel network'}</em>
                </span>
                <span className={`bd-brow-n ${(countBySlug.get(TUNNELS) || 0) ? 'has' : ''}`}>
                  {countBySlug.get(TUNNELS) || 0}
                </span>
              </button>
            )}
            {buildings.map(b => {
              const n = countBySlug.get(b.slug) || 0;
              return (
                <button key={b.slug} className={`bd-brow ${b.slug === slug ? 'on' : ''}`}
                        onClick={() => {
                          if (dirty && b.slug !== slug &&
                              !window.confirm('You have unsaved signs in this building. Switch anyway?')) return;
                          setCampus(false); setSlug(b.slug); setSelNode('');
                        }}>
                  <span className="bd-brow-name">{b.name || prettifySlug(b.slug)}</span>
                  <span className={`bd-brow-n ${n ? 'has' : ''}`}>{n}</span>
                </button>
              );
            })}
            {!buildings.length && (
              <div className="bd-empty">Your account has no buildings assigned yet.</div>
            )}
          </div>
        </aside>

        <main className="bd-main">
          {!slug && !campus && (
            <div className="bd-empty bd-pick">
              Pick a building on the left. Signs are placed, listed and printed one building
              at a time — which is how they are surveyed, and how they are walked round and put up.
            </div>
          )}

          {(slug || campus) && (
            <>
              <div className="bd-head">
                <span className="bd-shown">
                  {campus ? 'Tunnels & every first floor' : nameOf(slug)}
                </span>
                {campus && campusLoad.total > 0 && campusLoad.done < campusLoad.total && (
                  <span className="bd-hint">
                    loading floors… {campusLoad.done}/{campusLoad.total}
                  </span>
                )}
                {/* Placing signs is not a mode you switch into — it is what the
                    screen does. So there is no button for it: the map is always
                    there, and Print is the one place you go and come back from. */}
                {/* No Print in the campus view. The printed sheet leads with a
                    straightened floor-plan guide for ONE building — that is what
                    somebody carries down a corridor — and there is no such plan
                    for "everywhere". Signs placed here are printed from the
                    building they were filed under, which is where somebody
                    putting them up would look for them anyway. */}
                <div className="bd-panes">
                  {campus
                    ? <span className="bd-hint">print from a building</span>
                    : view === 'place'
                      ? <button className="bd-pane" onClick={() => setView('print')}
                                disabled={!buildingCount}>
                          Print{buildingCount ? ` (${buildingCount})` : ''}
                        </button>
                      : <button className="bd-pane" onClick={() => setView('place')}>← Back</button>}
                </div>
                {view === 'place' && (
                  <span className="bd-hint">click a node or a room to place a sign · click it again to remove</span>
                )}
              </div>

              {/* ── Place: the map, and a click is the whole interaction ── */}
              {view === 'place' && (
                <>
                  <div className="bd-map-wrap">
                    <div className="bd-map" ref={attachMapDiv} />

                    {/* Top left, over the map — the switch that changes what is
                        drawn, next to the thing it changes. */}
                    <div className="bd-mapctrls">
                      {/* Not offered in the campus view: room polygons are the
                          4 MB of struct files this view deliberately does not
                          load, so the button would toggle nothing. */}
                      {!campus && (
                        <button className={`bd-only ${showRoomNumbers ? 'on' : ''}`}
                                onClick={() => setShowRoomNumbers(v => !v)}>
                          {showRoomNumbers ? 'Hide' : 'Show'} room numbers
                        </button>
                      )}
                      {campus && zoom < CAMPUS_NODE_ZOOM && (
                        <span className="bd-zoomhint">Zoom in to place signs inside buildings</span>
                      )}
                    </div>

                    {/* A vertical stack up the right of the map: floors ARE a
                        stack, and reading them as one costs nothing to learn. */}
                    {!campus && !!floors.length && (
                      <div className="bd-floors">
                        {floors.map(f => (
                          <button key={f} className={`bd-floor ${f === floor ? 'on' : ''}`}
                                  onClick={() => { setFloor(f); setSelNode(''); }}>
                            {floorLabel(f)}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="bd-legend">
                      {campus && (
                        <span><i style={{ background: TUNNEL_COLOR }} /> tunnel</span>
                      )}
                      <span><i style={{ background: NODE_COLOR }} /> node</span>
                      {/* Square, and outlined: a room is a shape you click, not
                          another dot, and the fill alone is near-white. */}
                      <span><i style={{ background: POLY_OPTS.room.fill, border: `1px solid ${POLY_OPTS.room.stroke}`,
                                        borderRadius: 2, boxSizing: 'border-box' }} /> room</span>
                      <span><i style={{ background: SIGN_COLOR }} /> has a sign</span>
                      <span><i style={{ background: SEL_COLOR }} /> selected</span>
                    </div>
                  </div>
                </>
              )}

              {/* ── Print: the guide pages, then the signs ── */}
              {view === 'print' && (
                <div className="bd-print">
                  <div className="bd-print-ctrls">
                    <div className="bd-perpage">
                      <span className="bd-perpage-label">Per page</span>
                      {[[1, 'One'], [2, 'Two side by side']].map(([n, l]) => (
                        <button key={n} className={`bd-pane ${perPage === n ? 'on' : ''}`}
                                onClick={() => setPerPage(n)}>{l}</button>
                      ))}
                    </div>
                    <button className="bd-btn bd-primary" onClick={printSheet} disabled={!toPrint.length}>
                      Print the sheet ({toPrint.length})
                    </button>
                  </div>
                  <div className="bd-hint bd-blockhint">
                    The sheet leads with <strong>floor-plan guide pages</strong> showing where each sign
                    goes, then the signs themselves. Each encodes{' '}
                    <code>?n=&lt;nodeId&gt;&amp;s=&lt;identifier&gt;</code>, so a sign works the moment it
                    is printed — nothing has to be published first.
                  </div>

                  {!!guideFloors.length && (
                    <div className="bd-guide">
                      <div className="bd-guide-title">
                        Placement guide — {guideFloors.length} floor{guideFloors.length === 1 ? '' : 's'}
                      </div>
                      <div className="bd-guide-grid">
                        {guideFloors.map(({ floorNum, plan }) => {
                          const fs = toPrint.filter(s => s.floor === floorNum);
                          const wide = perPage !== 2 && plan.aspect > 1.6;
                          const box = perPage === 2 ? { w: 560, h: 700 }
                            : wide ? { w: 940, h: 400 } : { w: 760, h: 720 };
                          return (
                            <figure key={floorNum} className={`bd-plan${wide ? ' wide' : ''}`}>
                              <figcaption>Floor {floorTag(floorNum)} · {fs.length} sign{fs.length === 1 ? '' : 's'}</figcaption>
                              <div className="bd-plan-body">
                                <div className="bd-plan-svg" style={{ aspectRatio: `${box.w} / ${box.h}` }}
                                     dangerouslySetInnerHTML={{ __html: planSvg(plan, {
                                       ...box,
                                       // `label`, not `n`: the plan reads label/code, so
                                       // the old `n` drew bare dots while the legend beside
                                       // them listed numbers that pointed at nothing.
                                       marks: fs.map((s, i) => ({ nodeId: s.nodeId, label: String(i + 1) })),
                                     }) }} />
                                <ol className="bd-plan-legend">
                                  {fs.map((s, i) => (
                                    <li key={s.id}><b>{i + 1}</b> <span className="lg-code">{s.code}</span>
                                      {s.location && <span className="lg-where">{s.location}</span>}</li>
                                  ))}
                                </ol>
                              </div>
                            </figure>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className={`bd-sheet per-${perPage}`}>
                    {toPrint.map(s => (
                      <figure key={s.id} className="bd-sign">
                        <h4>Find your way<br />around campus!</h4>
                        <div className="bd-sign-qr">
                          <div dangerouslySetInnerHTML={{ __html: signQrSvg(signUrl(s, DEFAULT_BASE), 480) }} />
                          <img className="bd-sign-badge" src={LOGO_URL} alt="" />
                        </div>
                        <div className="bd-sign-addr">
                          {String(DEFAULT_BASE).replace(/^https?:\/\//, '').replace(/\/+$/, '')}
                        </div>
                        <div className="bd-sign-node">{s.nodeId}</div>
                        {/* A room sign says where it is; a corridor sign has
                            only its identifier. Same rule as the print sheet —
                            see identOf() there. */}
                        <div className={`bd-sign-code${s.location ? ' where' : ''}`}>
                          {s.location ? `${nameOf(slug)}, ${s.location}` : s.code}
                        </div>
                      </figure>
                    ))}
                    {!toPrint.length && (
                      <div className="bd-empty">Nothing ticked for printing in {nameOf(slug)}.</div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

// Self-contained styles, the same way PhotoTrackerPage carries its own: this
// page is one route with no shared chrome, and a stylesheet import would put its
// rules on every other page in the bundle.
/**
 * Who may edit which buildings.
 *
 * Assigning access used to mean editing BUILDING_SCOPES and redeploying, which
 * in practice meant nobody ever narrowed anybody and every account could place
 * signs anywhere on campus. This is the screen that makes it a thing somebody
 * can do while a faculty is on the phone asking for it.
 *
 * Two things it deliberately does NOT do: create accounts and delete them. Who
 * may log in stays an env-var decision, because handing out logins from a web
 * page is a different act from adjusting what an existing login reaches, and
 * only the second one belongs in a browser.
 */
function AccountManager({ token, buildings, onStatus, onError }) {
  const [accounts, setAccounts] = useState(null);
  const [admin, setAdmin] = useState('');
  const [openName, setOpenName] = useState('');
  const [draft, setDraft] = useState(null);      // '*' or [slug] for the open account
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState('');

  const call = useCallback(async (body = null) => {
    const r = await fetch(`${API_BASE}/api/building-scopes`, {
      method: body ? 'POST' : 'GET',
      headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `Request failed (${r.status})`);
    return j;
  }, [token]);

  const load = useCallback(() => {
    call()
      .then(d => { setAccounts(d.accounts || []); setAdmin(d.admin || ''); })
      .catch(e => { setAccounts([]); onError?.(e.message); });
  }, [call, onError]);

  useEffect(load, [load]);

  const open = (a) => {
    setOpenName(a.name);
    setDraft(a.scope === '*' ? '*' : [...(a.scope || [])]);
    setQ('');
  };

  const toggle = (slug) => setDraft(d => {
    const list = d === '*' ? buildings.map(b => b.slug) : [...d];
    const i = list.indexOf(slug);
    if (i >= 0) list.splice(i, 1); else list.push(slug);
    return list;
  });

  const save = async (name) => {
    setSaving(name); onError?.('');
    try {
      const d = await call({ account: name, scope: draft });
      onStatus?.(`${name} — ${d.scope === '*' ? 'all buildings' : `${d.scope.length} building${d.scope.length === 1 ? '' : 's'}`}.`);
      setOpenName('');
      load();
    } catch (e) { onError?.(e.message); }
    setSaving('');
  };

  const rows = buildings.filter(b => {
    const t = q.trim().toLowerCase();
    return !t || `${b.name || ''} ${b.slug}`.toLowerCase().includes(t);
  });

  const summary = (a) => {
    if (a.scope === '*') return 'All buildings';
    const n = (a.scope || []).length;
    return n ? `${n} building${n === 1 ? '' : 's'}` : 'No buildings';
  };

  return (
    <div className="bd-accounts">
      <div className="bd-acchead">
        <h2>Account access</h2>
        <p>
          Which buildings each account may place and print signs in. Changes take effect
          immediately — an account already signed in loses a building on its next save,
          not when its session expires.
        </p>
      </div>

      {accounts === null && <div className="bd-empty">Loading accounts…</div>}
      {accounts?.length === 0 && (
        <div className="bd-empty">
          No accounts are configured. Make one on the Accounts screen in <code>/admin</code>,
          or add it to <code>BUILDING_EDITORS</code> (or <code>TOUR_EDITORS</code>) in the
          environment.
        </div>
      )}

      {/* Making a login and deciding what it reaches are the same job, so the
          maker sits above the list it will add to. */}
      <AccountMaker token={token} kind="buildings" what="a faculty will" />

      {accounts?.map(a => {
        const isTheAdmin = a.name === admin;
        const isOpen = openName === a.name;
        return (
          <div key={a.name} className={`bd-acc ${isOpen ? 'on' : ''}`}>
            <div className="bd-accrow">
              <div className="bd-accname">
                <strong>{a.name}</strong>
                <span className="bd-acctags">
                  <span className={`bd-tag ${a.source === 'tour' ? 'alt' : ''}`}>
                    {a.source === 'tour' ? 'Tour Creator account' : 'Building account'}
                  </span>
                  {/* Buildings can be assigned to any account here, but only a
                      stored one can be renamed or deleted on /admin — an env
                      login needs a Vercel change, so say which this is. */}
                  {a.origin === 'env' && <span className="bd-tag alt">env login</span>}
                  {/* Where the current value came from. "BUILDING_SCOPES" matters:
                      it is set in the environment, so it is not what this screen
                      last saved and will not change until it is assigned here. */}
                  {a.from === 'env' && <span className="bd-tag alt">from BUILDING_SCOPES</span>}
                  {a.from === 'default' && <span className="bd-tag alt">never restricted</span>}
                </span>
              </div>
              <span className="bd-accsum">{isTheAdmin ? 'All buildings (admin)' : summary(a)}</span>
              {isTheAdmin ? (
                <span className="bd-hint">always</span>
              ) : (
                <button className="bd-btn" onClick={() => (isOpen ? setOpenName('') : open(a))}>
                  {isOpen ? 'Cancel' : 'Change'}
                </button>
              )}
            </div>

            {isOpen && (
              <div className="bd-accedit">
                <div className="bd-accmodes">
                  <button className={`bd-btn ${draft === '*' ? 'bd-primary' : ''}`}
                          onClick={() => setDraft('*')}>All buildings</button>
                  <button className={`bd-btn ${draft !== '*' ? 'bd-primary' : ''}`}
                          onClick={() => setDraft(draft === '*' ? [] : draft)}>Only the ones I pick</button>
                </div>

                {draft !== '*' && (
                  <>
                    <input className="bd-input" placeholder="Find a building…" value={q}
                           onChange={e => setQ(e.target.value)} />
                    <div className="bd-acclist">
                      {rows.map(b => (
                        <label key={b.slug} className="bd-accitem">
                          <input type="checkbox" checked={draft.includes(b.slug)}
                                 onChange={() => toggle(b.slug)} />
                          <span>{b.name || prettifySlug(b.slug)}</span>
                        </label>
                      ))}
                      {!rows.length && <div className="bd-empty">No building matches.</div>}
                    </div>
                    <div className="bd-hint">
                      {draft.length
                        ? `${draft.length} selected`
                        : 'Nothing selected — this account will see no buildings at all.'}
                    </div>
                  </>
                )}

                <div className="bd-accactions">
                  <button className="bd-btn bd-primary" disabled={saving === a.name}
                          onClick={() => save(a.name)}>
                    {saving === a.name ? 'Saving…' : 'Save access'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const CSS = ACCOUNT_MAKER_CSS + `
/* TWO palettes, one set of tokens. Every surface below reads a token and none
   names a colour, which is the only reason a theme switch is a class on the
   root rather than a rewrite: adding .light re-points the same nine variables.
   --field (an input well, set apart from the card it sits on) and --on-accent
   (what reads ON the accent) are tokens because the shared AccountMaker is used
   by this tool AND by the light Event Creator, and a literal would be right in
   exactly one of them.

   The accent is UM Blue (UMAPS_BRANDING_GUIDE.md §2). Light gets #0060a8 as
   drawn; dark gets it lifted to #4da3e8, because the brand value is a 2.5:1
   whisper as text on near-black and this tool puts the accent on text. Same
   colour, corrected for the ground it sits on. */
.bd-root, .bd-login { --bg:#15171a; --panel:#1d2024; --line:#2b2f35; --line-2:#3a3f47;
  --text:#e8eaed; --dim:#9aa0a6; --field:#111316; --btn:#24282e; --hover:#22262b;
  --inset:#191c20; --float:#1d2024ee; --danger:#ff8a80;
  --accent:#4da3e8; --accent-soft:rgba(77,163,232,.16); --on-accent:#08243a;
  color:var(--text); background:var(--bg);
  font:14px/1.45 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
.bd-root.light, .bd-login.light { --bg:#f3f4f6; --panel:#ffffff; --line:#dde1e6; --line-2:#c3c9d0;
  --text:#1b1f24; --dim:#5f6368; --field:#f7f8fa; --btn:#ffffff; --hover:#eceff3;
  --inset:#f7f8fa; --float:#ffffffee; --danger:#c5221f;
  --accent:#0060a8; --accent-soft:rgba(0,96,168,.10); --on-accent:#ffffff; }
.bd-login { min-height:100vh; display:flex; align-items:center; justify-content:center; padding:24px; }
.bd-card { background:var(--panel); border:1px solid var(--line); border-radius:12px;
  padding:24px; width:min(360px,100%); display:flex; flex-direction:column; gap:10px; }
.bd-card h1 { margin:0; font-size:19px; }
.bd-card p { margin:0 0 6px; color:var(--dim); font-size:13px; }
.bd-err { color:var(--danger); font-size:13px; }
.bd-note { margin:2px 0 0 !important; color:var(--dim); font-size:12px; }

.bd-root { min-height:100vh; display:flex; flex-direction:column; }
.bd-top { display:flex; align-items:center; gap:12px; padding:10px 14px;
  border-bottom:1px solid var(--line); background:var(--panel); flex:none; flex-wrap:wrap; }
.bd-who { color:var(--dim); font-size:12px; }
.bd-topright { margin-left:auto; display:flex; align-items:center; gap:12px; }
.bd-status { color:var(--dim); font-size:12px; }

/* Light/dark, as a track the knob slides along. Both glyphs stay on the track
   and the knob slides BEHIND them to sit under the one in force, which then
   reads white on the accent — so the switch says which mode you are in and
   what the other one is, without a word of label.

   The knob marks the current state, not the destination. A switch whose handle
   points at where you are going is the one people press twice. */
.bd-theme { position:relative; flex:none; width:52px; height:26px; padding:0;
  border:1px solid var(--line); border-radius:999px; background:var(--field);
  cursor:pointer; display:flex; align-items:center; justify-content:space-between; }
.bd-theme-ico { position:relative; z-index:1; width:13px; height:13px; margin:0 5px;
  fill:none; stroke:var(--dim); stroke-width:2; stroke-linecap:round; }
.bd-theme-ico.moon { fill:var(--dim); stroke:none; }
.bd-theme.on .bd-theme-ico.sun { stroke:var(--on-accent); }
.bd-theme:not(.on) .bd-theme-ico.moon { fill:var(--on-accent); }
.bd-theme-knob { position:absolute; z-index:0; top:2px; left:2px; width:20px; height:20px;
  border-radius:50%; background:var(--accent); transform:translateX(26px);
  transition:transform .16s ease; }
.bd-theme.on .bd-theme-knob { transform:translateX(0); }
@media (prefers-reduced-motion: reduce) { .bd-theme-knob { transition:none; } }
.bd-status-err { color:var(--danger); }

.bd-input { background:var(--field); color:var(--text); border:1px solid var(--line);
  border-radius:6px; padding:7px 9px; font:inherit; width:100%; }
.bd-input:focus { outline:none; border-color:var(--accent); }
.bd-btn { background:var(--btn); color:var(--text); border:1px solid var(--line);
  border-radius:6px; padding:6px 12px; font:inherit; cursor:pointer; }
.bd-btn:hover:not(:disabled) { border-color:var(--accent); }
.bd-btn:disabled { opacity:.5; cursor:default; }
.bd-primary { background:var(--accent); color:var(--on-accent); border-color:var(--accent); font-weight:600; }
.bd-del:hover { border-color:var(--danger); color:var(--danger); }

.bd-body { flex:1; min-height:0; display:flex; }
/* The hidden attribute alone would do nothing here: the UA's rule for it loses
   to the class rule above, so the sign tool would stay on screen behind the
   accounts screen. It is hidden rather than unmounted so the Google map, its
   tiles and the loaded building geometry survive a trip to Accounts. */
.bd-body[hidden] { display:none; }
.bd-side { width:240px; flex:none; border-right:1px solid var(--line); padding:10px;
  display:flex; flex-direction:column; gap:8px; min-height:0; }
.bd-blist { flex:1; min-height:0; overflow:auto; display:flex; flex-direction:column; gap:2px; }
.bd-brow { display:flex; align-items:center; gap:8px; text-align:left; background:none;
  border:1px solid transparent; border-radius:6px; padding:6px 8px; color:var(--text);
  font:inherit; cursor:pointer; }
.bd-brow:hover { background:var(--hover); }
.bd-brow.on { background:var(--accent-soft); border-color:var(--accent); }
.bd-brow-name { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.bd-brow-n { flex:none; font-size:11px; color:var(--dim); }
.bd-brow-n.has { color:var(--accent); font-weight:700; }

/* The campus row is a different KIND of thing from the buildings under it — a
   way of looking, not a place — so it is set apart by a rule rather than by
   sitting first in the same list, where it would read as a building called
   "Tunnels". */
.bd-campus { margin-bottom:6px; padding-bottom:8px; border-bottom:1px solid var(--line); border-radius:6px; }
.bd-campus .bd-brow-name { white-space:normal; }
.bd-campus em { display:block; font-style:normal; font-size:11px; color:var(--dim); margin-top:1px; }
.bd-campus.on em { color:var(--accent); }
.bd-zoomhint { font-size:11px; color:var(--dim); background:var(--panel); border:1px solid var(--line);
  border-radius:6px; padding:4px 8px; }

.bd-main { flex:1; min-width:0; display:flex; flex-direction:column; min-height:0; }
.bd-head { display:flex; align-items:center; gap:12px; padding:10px 14px;
  border-bottom:1px solid var(--line); flex:none; flex-wrap:wrap; }
.bd-shown { font-weight:600; }
.bd-panes { display:flex; gap:4px; }
.bd-pane, .bd-floor { background:var(--btn); color:var(--text); border:1px solid var(--line);
  border-radius:6px; padding:5px 11px; font:inherit; cursor:pointer; }
.bd-pane.on, .bd-floor.on { background:var(--accent); color:var(--on-accent); border-color:var(--accent); }
.bd-hint { color:var(--dim); font-size:12px; }
.bd-blockhint { padding:0 16px 10px; }
.bd-hint code { background:var(--field); padding:1px 4px; border-radius:3px; }
.bd-empty { color:var(--dim); padding:16px; }
.bd-pick { max-width:440px; margin:40px auto; text-align:center; line-height:1.6; }

.bd-map-wrap { flex:1; min-height:0; position:relative; }
.bd-map { position:absolute; inset:0; }

/* The two switches that change what is drawn, top left over the map. */
.bd-mapctrls { position:absolute; left:12px; top:12px; z-index:5;
  display:flex; flex-direction:column; align-items:flex-start; gap:6px; }
.bd-only { background:var(--float); color:var(--text); border:1px solid var(--line);
  border-radius:6px; padding:6px 11px; font:inherit; font-size:13px; cursor:pointer;
  white-space:nowrap; }
.bd-only.on { border-color:var(--accent); color:var(--accent); }

/* Floors as a vertical stack up the right of the map — which is the shape the
   thing itself has, so there is nothing to learn. Bottom floor at the bottom,
   for the same reason. */
.bd-floors { position:absolute; right:12px; top:12px; z-index:5;
  display:flex; flex-direction:column-reverse; gap:3px;
  background:var(--float); border:1px solid var(--line); border-radius:8px; padding:4px;
  max-height:calc(100% - 90px); overflow:auto; }
.bd-floor { min-width:38px; padding:6px 8px; text-align:center; border-color:transparent;
  background:transparent; font-size:13px; font-weight:600; }
.bd-floor:hover { background:var(--hover); }
.bd-floor.on:hover { background:var(--accent); }

.bd-legend { position:absolute; left:12px; bottom:12px; z-index:5;
  display:flex; gap:12px; padding:6px 10px; border-radius:8px;
  background:var(--float); border:1px solid var(--line);
  color:var(--dim); font-size:12px; flex-wrap:wrap; }
.bd-legend span { display:flex; align-items:center; gap:5px; }
.bd-legend i { width:9px; height:9px; border-radius:50%; display:inline-block; }

.bd-list-body { flex:1; min-height:0; overflow:auto; padding:12px 14px; }
.bd-listbar { max-width:360px; margin-bottom:10px; }
.bd-list { display:flex; flex-direction:column; gap:10px; }
.bd-item { display:flex; gap:14px; align-items:flex-start; background:var(--panel);
  border:1px solid var(--line); border-radius:10px; padding:10px; }
.bd-item.off { opacity:.55; }
.bd-item-qr { flex:none; width:108px; background:#fff; border-radius:6px; padding:4px; line-height:0; }
.bd-item-fields { flex:1; min-width:0; display:grid; gap:8px;
  grid-template-columns:repeat(auto-fit, minmax(160px,1fr)); }
.bd-item-fields label { display:flex; flex-direction:column; gap:3px;
  font-size:11px; color:var(--dim); }
.bd-item-meta { flex:none; width:150px; display:flex; flex-direction:column; gap:4px;
  font-size:11px; color:var(--dim); }
.bd-item-meta a { color:var(--accent); }
.bd-item-meta code { font-size:10px; }
.bd-dupe { color:var(--danger); margin-left:6px; }
.bd-item-actions { flex:none; display:flex; flex-direction:column; gap:6px; }
.bd-check { font-size:12px; color:var(--dim); display:flex; align-items:center; gap:5px; }

.bd-print { flex:1; min-height:0; overflow:auto; }
.bd-print-ctrls { display:flex; align-items:flex-end; gap:14px; padding:12px 16px; flex-wrap:wrap; }
.bd-perpage { display:flex; align-items:center; gap:6px; }
.bd-perpage-label, .bd-baselabel { color:var(--dim); font-size:11px; }
.bd-baselabel { display:flex; flex-direction:column; gap:3px; min-width:260px; }

/* Previews are on white for the same reason the print sheet is: these are
   printed pages, previewed inside a dark tool, and showing them in dark chrome
   would misrepresent every contrast decision in them. */
.bd-guide { padding:6px 16px 4px; }
.bd-guide-title { color:var(--dim); font-size:11px; margin-bottom:10px; }
.bd-guide-grid { display:grid; gap:14px; grid-template-columns:repeat(auto-fill, minmax(320px,1fr)); }
.bd-plan { margin:0; background:#fff; color:#000; border-radius:8px; padding:10px;
  font-family:"Helvetica Neue", Helvetica, Arial, sans-serif; }
.bd-plan.wide { grid-column:1 / -1; }
.bd-plan figcaption { font-size:12px; font-weight:700; margin-bottom:6px; }
.bd-plan-body { display:flex; gap:10px; align-items:stretch; }
.bd-plan.wide .bd-plan-body { flex-direction:column; }
.bd-plan-svg { flex:1; min-width:0; border:1px solid #e2e2e2; border-radius:6px; overflow:hidden; }
.bd-plan-svg svg { width:100%; height:100%; display:block; }
.bd-plan-legend { flex:none; width:40%; margin:0; padding:0; list-style:none;
  font-size:10.5px; line-height:1.5; }
.bd-plan.wide .bd-plan-legend { width:auto; columns:3; column-gap:14px; }
.bd-plan-legend li { margin-bottom:4px; break-inside:avoid; }
.bd-plan-legend b { display:inline-block; min-width:14px; height:14px; line-height:13px;
  margin-right:4px; text-align:center; border-radius:50%;
  border:1px solid #c2185b; color:#c2185b; font-size:8.5px; }
.bd-plan-legend .lg-code { font-weight:700; letter-spacing:.06em; }
.bd-plan-legend .lg-where { display:block; margin-left:18px; color:#777; }
.bd-plan.wide .bd-plan-legend .lg-where { display:inline; margin-left:0; }

.bd-sheet { display:grid; gap:18px; padding:16px; justify-content:center;
  grid-template-columns:minmax(0,300px); }
.bd-sheet.per-2 { grid-template-columns:repeat(2, minmax(0,250px)); }
.bd-sign { margin:0; position:relative; background:#fff; color:#000;
  border:1.5px solid #0060a8; border-radius:10px; padding:18px 14px 16px; text-align:center;
  font-family:"Helvetica Neue", Helvetica, Arial, sans-serif; }
.bd-sign h4 { margin:0 0 16px; font-size:25px; font-weight:800; line-height:1.08;
  letter-spacing:-.01em; color:#000; }
.bd-sheet.per-2 .bd-sign h4 { font-size:21px; margin-bottom:13px; }
/* The badge is centred on the SYMBOL, so it shares a positioning context with
   the SVG rather than with the card — the two differ by the quiet zone. */
.bd-sign-qr { position:relative; width:78%; margin:0 auto; line-height:0; }
.bd-sign-qr svg { width:100%; height:auto; display:block; }
/* The width comes from qrBadge.js (MARK_CSS_WIDTH), never a number typed here:
   the on-screen preview and the sheet that prints have to be the same mark. */
.bd-sign-badge { position:absolute; left:50%; top:50%; transform:translate(-50%,-50%);
  width:${MARK_CSS_WIDTH}; height:auto; display:block; }
.bd-sign-addr { margin-top:14px; font-size:17px; font-weight:800; color:#000; }
.bd-sheet.per-2 .bd-sign-addr { font-size:14px; margin-top:11px; }
/* Bottom left, opposite the identifier, and nearly invisible: it is here so a
   sign can be checked or typed by hand without decoding the symbol, not for a
   student to read. */
.bd-sign-node { position:absolute; left:8px; bottom:6px; max-width:58%;
  font-family:ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size:7px; line-height:1.2; color:#c9c9c9; text-align:left; word-break:break-all; }
.bd-sign-code { position:absolute; right:8px; bottom:6px; font-size:9.5px; font-weight:700;
  letter-spacing:.14em; color:#dcdcdc; }
.bd-sign-code.where { letter-spacing:.02em; font-size:8px; max-width:60%; text-align:right; }

@media (max-width: 760px) {
  .bd-body { flex-direction:column; }
  .bd-side { width:auto; border-right:none; border-bottom:1px solid var(--line); max-height:200px; }
  .bd-item { flex-wrap:wrap; }
}

/* ── Account access ──
   Written light while the rest of the tool is dark, this screen read as a sheet
   of paper dropped into it. Everything below is on the same tokens as the sign
   tool, so the two cannot drift apart again. */
.bd-accounts { flex:1; min-height:0; overflow:auto; padding:22px 20px 40px; max-width:860px;
  margin:0 auto; width:100%; box-sizing:border-box; }
.bd-acchead h2 { margin:0 0 4px; font-size:19px; }
.bd-acchead p { margin:0 0 18px; color:var(--dim); font-size:13.5px; max-width:62ch; }
.bd-acc { border:1px solid var(--line); border-radius:12px; margin-bottom:10px;
  background:var(--panel); overflow:hidden; transition:border-color .12s; }
.bd-acc:hover { border-color:var(--line-2); }
.bd-acc.on, .bd-acc.on:hover { border-color:var(--accent); }
.bd-accrow { display:flex; align-items:center; gap:12px; padding:12px 14px; }
.bd-accname { flex:1; min-width:0; display:flex; flex-direction:column; gap:3px; }
.bd-accname strong { font-size:14.5px; }
.bd-acctags { display:flex; gap:6px; flex-wrap:wrap; }
.bd-tag { font-size:11px; font-weight:700; background:var(--accent-soft); color:var(--accent);
  border-radius:999px; padding:2px 8px; }
.bd-tag.alt { background:var(--btn); color:var(--dim); }
.bd-accsum { font-size:13px; color:var(--dim); flex:none; }
.bd-accedit { border-top:1px solid var(--line); padding:14px; display:flex;
  flex-direction:column; gap:10px; background:var(--inset); }
.bd-accmodes { display:flex; gap:8px; flex-wrap:wrap; }
.bd-acclist { display:grid; gap:2px; grid-template-columns:repeat(auto-fill, minmax(220px,1fr));
  max-height:300px; overflow:auto; background:var(--field);
  border:1px solid var(--line); border-radius:10px; padding:8px; }
.bd-accitem { display:flex; align-items:center; gap:8px; padding:5px 7px; border-radius:7px;
  font-size:13.5px; color:var(--text); cursor:pointer; }
.bd-accitem:hover { background:var(--hover); }
.bd-accactions { display:flex; gap:8px; }
/* Without this a checkbox is painted by the platform in its light default —
   which is most of what made this screen read as white. */
.bd-root input[type="checkbox"] { accent-color:var(--accent); }
/* The account maker is one of these cards, not a bare outline sitting on the
   page background. */
.bd-accounts .am { background:var(--panel); }
`;
